/**
 * V4 authorisation rules for the current user, from `GET /v1/permissions`
 * (CurrentPermissionsResponse):
 *
 *   {
 *     "permissions": {"07b699f1-…": ["*"], "5153df4f-…": ["read.devices.*"]},
 *     "translations": []
 *   }
 *
 * A rule reads `<action>.<subject>[.<resource-id>]`; `*` is a wildcard at any
 * position. `permissions` covers every account the user can reach, keyed by
 * account id. `translations` is the separate list of locale ids whose
 * translations may be edited (`["*"]` = all) — a `*` rule does NOT imply it.
 *
 * Dependency-free so the API layer and the User model can both import it.
 */

export const V4Action = {
  Read: 'read',
  Update: 'update',
  Manage: 'manage',
};

// PermissionsMetadataResponse.subjects, limited to the ones we gate on. The
// literal strings are the platform's own — singular and plural mix (`arc`,
// `sim_card` vs `whitelabels`, `recurrings`), so copy them, don't derive them.
export const V4Subject = {
  Accounts: 'accounts',
  Administration: 'administration',
  Arc: 'arc',
  Devices: 'devices',
  Recurrings: 'recurrings',
  Scenarios: 'scenarios',
  SimCard: 'sim_card',
  Teams: 'teams',
  Translations: 'translations',
  Users: 'users',
  Whitelabels: 'whitelabels',
};

// read < update < manage. An unrecognised verb (a future `create`, `delete`, …)
// counts as write-level, so an unknown vocabulary can never hide UI.
const ACTION_RANK = {
  [V4Action.Read]: 1,
  [V4Action.Update]: 2,
  [V4Action.Manage]: 3,
};
const rankOf = action => ACTION_RANK[action] ?? ACTION_RANK[V4Action.Manage];

const lc = value => String(value ?? '').toLowerCase();

// An absent segment on either side means "anything".
const segmentMatches = (ruleSegment, wanted) =>
  wanted === undefined ||
  wanted === null ||
  ruleSegment === undefined ||
  ruleSegment === '*' ||
  lc(ruleSegment) === lc(wanted);

function ruleGrants(rule, action, subject, resourceId) {
  const [ruleAction, ruleSubject, ruleResource] = lc(rule).split('.');
  if (!ruleAction) return false;
  if (ruleAction !== '*' && rankOf(ruleAction) < rankOf(lc(action))) return false;
  if (!segmentMatches(ruleSubject, subject)) return false;
  return segmentMatches(ruleResource, resourceId);
}

const toRules = rules =>
  Array.isArray(rules) ? rules.filter(rule => typeof rule === 'string' && !!rule) : [];

// Keys are account uuids, or `*` for every account the user can reach.
const ACCOUNT_WILDCARD = '*';
const isAccountKey = key =>
  key === ACCOUNT_WILDCARD ||
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(key);

/**
 * Normalises the response into `{byAccount, translations}`, or null when it
 * carries nothing we understand — which callers read as "unknown" and never as
 * "denied". `translations` stays null unless the payload listed them: an empty
 * list is a denial, an absent one is silence.
 */
export function normalizeV4Permissions(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const byAccount = {};
  const map = payload.permissions;
  if (map && typeof map === 'object' && !Array.isArray(map)) {
    Object.keys(map).forEach(accountId => {
      if (!isAccountKey(accountId) || !Array.isArray(map[accountId])) return;
      byAccount[lc(accountId)] = toRules(map[accountId]);
    });
  }
  const translations = Array.isArray(payload.translations) ? payload.translations : null;
  if (!Object.keys(byAccount).length && !translations) return null;
  return {byAccount, translations};
}

/**
 * Whether the payload named concrete accounts. A `*`-only map grants rights
 * without saying where, so it can't answer "which accounts?" — callers needing
 * that must list the accounts themselves.
 */
export const hasV4AccountRules = permissions =>
  Object.keys(permissions?.byAccount || {}).some(key => key !== ACCOUNT_WILDCARD);

/**
 * The rules that apply in `accountId` — its own entry, else the `*` bucket.
 * Empty means "unknown", not "nothing"; the checks below grant by default.
 * Exported for V4Users/permissions.js, which copies the literal strings.
 */
export const v4RulesFor = (permissions, accountId) => {
  const byAccount = permissions?.byAccount;
  if (!byAccount) return [];
  return (accountId && byAccount[lc(accountId)]) || byAccount[ACCOUNT_WILDCARD] || [];
};

/**
 * Every rule that applies in `accountId` — its own entry AND the `*` bucket.
 * {@link v4RulesFor}'s first-match is enough to answer "may I?"; copying a rule
 * set has to carry both, or a creator holding `read.devices.*` through the `*`
 * bucket hands the new user an entry naming no devices at all.
 */
export const v4AllRulesFor = (permissions, accountId) => {
  const byAccount = permissions?.byAccount;
  if (!byAccount) return [];
  return Array.from(new Set([
    ...(accountId ? byAccount[lc(accountId)] || [] : []),
    ...(byAccount[ACCOUNT_WILDCARD] || []),
  ]));
};

/** Whether the map has an entry of its own for `accountId` — not the `*` bucket. */
export const namesV4Account = (permissions, accountId) =>
  !!accountId && !!permissions?.byAccount?.[lc(accountId)];

/**
 * The map with `rules` written in for `accountId` — for a session that granted
 * itself rights the boot-time map cannot know about.
 *
 * Untouched whenever an entry would *narrow* it: null and `*`-only maps grant by
 * default, so naming one account would deny every other.
 */
export function withV4AccountRules(permissions, accountId, rules) {
  if (!accountId || !rules?.length) return permissions;
  if (!permissions || !hasV4AccountRules(permissions)) return permissions;
  return {
    ...permissions,
    byAccount: {...permissions.byAccount, [lc(accountId)]: toRules(rules)},
  };
}

/** Omit `subject` to ask "anything at this level", `resourceId` for the subject. */
export function hasV4Permission(permissions, accountId, action, subject, resourceId) {
  return v4RulesFor(permissions, accountId)
    .some(rule => ruleGrants(rule, action, subject, resourceId));
}

// `translations: []` means no locale is editable — hide the page. An absent list
// says nothing, and silence is not a denial; `["*"]` is every locale.
export const canEditV4Translations = permissions =>
  !Array.isArray(permissions?.translations) || permissions.translations.length > 0;

/**
 * Whether existing values of `localeId` may be edited. The backend resolves the
 * `translations` list in the default owner's rules; unknown denies.
 */
export function canUpdateV4Locale(permissions, localeId) {
  const listed = permissions?.translations;
  if (!localeId || !Array.isArray(listed)) return false;
  return listed.some(id => id === '*' || lc(id) === lc(localeId));
}

/**
 * Whether keys may be added to `localeId`. Needs an explicit `manage.translations`
 * rule, since bare `*` comes from customer accounts; an all-locales rule only
 * counts when the list is `["*"]`.
 */
export function canManageV4Locale(permissions, localeId) {
  if (!canUpdateV4Locale(permissions, localeId)) return false;
  const listsAll = permissions.translations.includes('*');
  return Object.values(permissions.byAccount || {}).some(rules => rules.some(rule => {
    const [, subject, resource] = lc(rule).split('.');
    if (subject !== V4Subject.Translations) return false;
    if (isAnySegment(resource) && !listsAll) return false;
    return ruleGrants(rule, V4Action.Manage, V4Subject.Translations, localeId);
  }));
}

/**
 * Whether `subject` may be managed in `accountId`. Rights are per entity, not per
 * account: `read.devices.* + manage.scenarios.*` is a valid set, and such an
 * account still belongs in every list — only the device actions are off limits.
 *
 * Anything the payload can't speak for — no rules, no account, or a map that
 * named no accounts at all — grants, so an unresolved rule set can never take a
 * capability away.
 */
export function canManageV4Subject(permissions, accountId, subject) {
  if (!accountId || !hasV4AccountRules(permissions)) return true;
  return hasV4Permission(permissions, accountId, V4Action.Manage, subject);
}

/**
 * Whether the account's own record — its settings — may be edited in
 * `accountId`. Same grant-by-default contract as {@link canManageV4Devices};
 * `read.accounts.<uuid>`, the end-customer shape, is deliberately not enough.
 */
export function canUpdateV4Account(permissions, accountId) {
  if (!accountId || !hasV4AccountRules(permissions)) return true;
  return hasV4Permission(permissions, accountId, V4Action.Update, V4Subject.Accounts);
}

/**
 * Archive, unarchive and delete: `manage.accounts` on `accountId`, read in its
 * owner's rules — or in any of `aboveIds`, the accounts above it: a grant
 * reaches every account beneath the one holding it, but the map only names the
 * holders. The account's own rules never count. Grants by default, like
 * {@link canUpdateV4Account}.
 */
export function canArchiveV4Account(permissions, ownerId, accountId, aboveIds = []) {
  if (!ownerId || !accountId || !hasV4AccountRules(permissions)) return true;
  const holders = [ownerId, ...(aboveIds || []).filter(id => !!id && lc(id) !== lc(accountId))];
  return holders.some(holderId => hasV4Permission(
    permissions,
    holderId,
    V4Action.Manage,
    V4Subject.Accounts,
    accountId,
  ));
}

/**
 * Whether the session manages `accountId` itself — `manage.accounts` held IN
 * that account and naming it (the resource is matched too, so a rule for
 * another account can't grant through the `*` bucket). Denies by default,
 * unlike the checks above: it hands out a manager-only control rather than
 * guarding one.
 */
export function managesV4Account(permissions, accountId) {
  if (!accountId) return false;
  return hasV4Permission(
    permissions,
    accountId,
    V4Action.Manage,
    V4Subject.Accounts,
    accountId,
  );
}

/**
 * Whether `accountId` carries a `manage.accounts` grant spelled out — the rule an
 * account manager is actually given. Stricter than {@link managesV4Account} on
 * purpose: a bare `*` does NOT answer for it, because that is precisely what the
 * platform writes for every Full-access user of an end customer (V4_ADMIN_RULES),
 * and the manager shell replaces the portal rather than adding to it.
 */
export function hasExplicitV4AccountManagement(permissions, accountId) {
  if (!accountId) return false;
  return v4RulesFor(permissions, accountId).some(rule => {
    const [action, subject, resource] = lc(rule).split('.');
    if (subject !== V4Subject.Accounts) return false;
    if (rankOf(action) < rankOf(V4Action.Manage)) return false;
    return segmentMatches(resource, accountId);
  });
}

/** Billing info: `manage.administration` in the top account. Denies by default. */
export function canManageV4Billing(permissions, topAccountId) {
  if (!topAccountId) return false;
  return hasV4Permission(permissions, topAccountId, V4Action.Manage, V4Subject.Administration);
}

/**
 * Whether `GET /v1/accounts` rows hold a customer of `ownAccountId` — a row whose
 * `owner_id` (V4's parent pointer) is that account. Parents and siblings the
 * token can read never count.
 */
export function ownsV4Accounts(accounts, ownAccountId) {
  const own = lc(ownAccountId);
  return !!own && Array.isArray(accounts) && accounts.some(a =>
    !!a?.id && !a.deleted_at && lc(a.id) !== own && lc(a.owner_id) === own);
}

// The rule spells the subject `arc`; the plural is accepted too, since every
// other subject in the vocabulary is plural.
const ARC_SUBJECTS = [V4Subject.Arc, 'arcs'];

/**
 * The one ARC an alarm-centre session is bound to, or null. The role has no flag
 * of its own — it *is* the rule set: `update.arc.<arc_id>` and nothing else, in
 * every account the token reaches. Any other subject makes this an ordinary
 * portal user; a `*` resource names no one ARC; silence is no role at all.
 */
export function v4ArcOnlyRole(permissions) {
  const byAccount = permissions?.byAccount;
  if (!byAccount) return null;
  let arcId = null;
  for (const accountId of Object.keys(byAccount)) {
    for (const rule of byAccount[accountId]) {
      const [, subject, resource] = lc(rule).split('.');
      if (!ARC_SUBJECTS.includes(subject)) return null;
      if (!resource || resource === '*') return null;
      // Two ARCs is not the single-ARC console this answers for.
      if (arcId && resource !== arcId) return null;
      arcId = resource;
    }
  }
  return arcId;
}
/** Whether devices may be created in `accountId` — see {@link canManageV4Subject}. */
export const canManageV4Devices = (permissions, accountId) =>
  canManageV4Subject(permissions, accountId, V4Subject.Devices);

const isAnySegment = segment => segment === undefined || segment === '' || segment === '*';

// Strict, unlike segmentMatches: a wildcard or missing wanted segment needs a wildcard held.
const segmentCovers = (held, wanted) =>
  isAnySegment(held) || (!isAnySegment(wanted) && held === wanted);

export function v4RuleCovers(heldRule, rule) {
  if (typeof heldRule !== 'string' || typeof rule !== 'string' || !heldRule || !rule) return false;
  const [heldAction, heldSubject, heldResource] = lc(heldRule).split('.');
  const [action, subject, resource] = lc(rule).split('.');
  if (heldAction !== '*') {
    if (isAnySegment(action)) return false;
    const bothKnown = heldAction in ACTION_RANK && action in ACTION_RANK;
    if (bothKnown ? rankOf(heldAction) < rankOf(action) : heldAction !== action) return false;
  }
  return segmentCovers(heldSubject, subject) && segmentCovers(heldResource, resource);
}

export const v4RulesCover = (heldRules, rule) =>
  Array.isArray(heldRules) && heldRules.some(held => v4RuleCovers(held, rule));

// Null when unknown, which means no cap: unknown permissions grant by default.
export function v4PermissionCeiling(permissions, accountId) {
  const rules = v4AllRulesFor(permissions, accountId);
  return rules.length ? rules : null;
}
